import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from 'utils'

const reportRequest = async (endpoint, params) => {
    const res = await axios({
        method: 'GET',
        url: getConfig.API_URL + endpoint,
        params,
        headers: {
            'Content-Type': 'application/json',
            Authorization: SessionManager.getCookie('user_token'),
        },
    })
    return res
}

export const fetchAdminReportDetail = createAsyncThunk(
    'adminReport/detail',
    async ({ endpoint, pageable: data, navigate }) => {
        const params = {
            premise_id: data?.premise_id,
            year_id: data?.year_id,
            month: data?.month,
            pageSize: data?.pageSize,
            pageNo: data?.currentPage,
        }

        if (data?.pdf) params.pdf = true
        if (data?.csv) params.csv = true

        try {
            const res = await reportRequest(endpoint, params)
            return res
        } catch (error) {
            return error.response
        }
    },
)

export const scheduledReportsList = createAsyncThunk(
    'adminReport/scheduledReports',
    async ({ premise_id, navigate }) => {
        try {
            const res = await reportRequest('scheduled_reports', { premise_id })
            return res
        } catch (error) {
            return error.response
        }
    },
)

export const downloadScheduledReport = createAsyncThunk(
    'adminReport/scheduledReportDownload',
    async ({
        premise_id,
        id,
        year_id,
        month,
        pdf,
        csv,
        navigate,
    }) => {
        const params = {
            premise_id,
            id,
        }

        if (year_id) params.year_id = year_id
        if (month) params.month = month
        if (pdf) params.pdf = true
        if (csv) params.csv = true

        try {
            const res = await reportRequest('scheduled_reports', params)
            return res
        } catch (error) {
            return error.response
        }
    },
)

export const adminReportSummary = createAsyncThunk(
    'adminReport/summary',
    async ({ pageable: data, navigate }) => {
        const params = {
            premise_id: data?.premise_id,
            year_id: data?.year_id,
            month: data?.month,
        }

        try {
            const [financialRes, occupancyRes, ticketRes, visitorRes] = await Promise.all([
                reportRequest('financial_report', params),
                reportRequest('occupancy_report', params),
                reportRequest('ticket_resolution_report', params),
                reportRequest('Visitor_traffic_report', params),
            ])

            return {
                financialRes,
                occupancyRes,
                ticketRes,
                visitorRes,
            }
        } catch (error) {
            return error.response
        }
    },
)

const adminReport = createSlice({
    name: 'adminReport',
    initialState: {
        financialReport: null,
        occupancyReport: null,
        ticketResolutionReport: null,
        visitorTrafficReport: null,
        scheduledReports: [],
        loading: false,
        scheduledReportsLoading: false,
    },
    reducers: {
        clearAdminReport: (state) => {
            state.financialReport = null
            state.occupancyReport = null
            state.ticketResolutionReport = null
            state.visitorTrafficReport = null
            state.scheduledReports = []
        },
    },
    extraReducers: (builder) => {
        builder.addCase(adminReportSummary.pending, (state) => {
            state.loading = true
        })
        builder.addCase(adminReportSummary.fulfilled, (state, action) => {
            state.loading = false
            const payload = action.payload

            if (payload?.financialRes?.status === 401 || payload?.status === 401) {
                showTokenExpiredAlert(payload?.data?.message, action.meta.arg.navigate)
                return
            }
            if (payload?.financialRes?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
                return
            }

            if (payload?.financialRes?.status === 200) {
                state.financialReport = payload.financialRes.data?.data?.financialReport || null
            } else {
                state.financialReport = null
            }

            if (payload?.occupancyRes?.status === 200) {
                state.occupancyReport = payload.occupancyRes.data?.data?.occupancyReport || null
            } else {
                state.occupancyReport = null
            }

            if (payload?.ticketRes?.status === 200) {
                state.ticketResolutionReport = payload.ticketRes.data?.data?.ticketResolutionReport || null
            } else {
                state.ticketResolutionReport = null
            }

            if (payload?.visitorRes?.status === 200) {
                state.visitorTrafficReport = payload.visitorRes.data?.data?.visitorTrafficReport || null
            } else {
                state.visitorTrafficReport = null
            }
        })
        builder.addCase(adminReportSummary.rejected, (state) => {
            state.loading = false
            state.financialReport = null
            state.occupancyReport = null
            state.ticketResolutionReport = null
            state.visitorTrafficReport = null
        })
        builder.addCase(scheduledReportsList.pending, (state) => {
            state.scheduledReportsLoading = true
        })
        builder.addCase(scheduledReportsList.fulfilled, (state, action) => {
            state.scheduledReportsLoading = false
            const payload = action.payload

            if (payload?.status === 401) {
                showTokenExpiredAlert(payload?.data?.message, action.meta.arg.navigate)
                state.scheduledReports = []
                return
            }
            if (payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
                state.scheduledReports = []
                return
            }

            if (payload?.status === 200) {
                state.scheduledReports = payload.data?.data?.list || []
            } else {
                state.scheduledReports = []
            }
        })
        builder.addCase(scheduledReportsList.rejected, (state) => {
            state.scheduledReportsLoading = false
            state.scheduledReports = []
        })
        builder.addCase(downloadScheduledReport.fulfilled, (state, action) => {
            const payload = action.payload

            if (payload?.status === 401) {
                showTokenExpiredAlert(payload?.data?.message, action.meta.arg.navigate)
                return
            }
            if (payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
                return
            }

            if (payload?.status === 200) {
                const list = payload.data?.data?.list
                if (Array.isArray(list) && list.length) {
                    state.scheduledReports = list
                }
            }
        })
    },
})

export const { clearAdminReport } = adminReport.actions
export default adminReport.reducer
